import type { SrCustomerStatus } from '@plato/data/starrocks/types';

// The Manticore path produces 'exact' | 'almost' | 'wildcard'; the new Search API returns an
// unvalidated match type that we pass through verbatim, so this is a plain string.
export type SearchMatchType = string;

// Per-row tracking id from the new Search API (one per hit). Absent on the Manticore
// path, where the client constructs it. request_id/version are response-level (per entity
// call / per search), not per row, so they live on the search response, not here.
export type SearchTrackingFields = {
    result_id?: string;
};

export type SearchMatchReason = {
    child_id?: string;
    field: string;
    child_type?: string;
    text: string;
};

export type CustomerMatchReasonField =
    | 'ERP_ID'
    | 'NAME'
    | 'ADDRESS.CITY'
    | 'ADDRESS.STREET'
    | 'ADDRESS.ZIP_CODE'
    | 'CONTACT.EMAIL'
    | 'CONTACT.MOBILE_NUMBER'
    | 'CONTACT.NAME'
    | 'CONTACT.PHONE_NUMBER';

export type CustomerMatchReasonChildType = 'address' | 'contact';

export type CustomerSearchRow = {
    id: string;
    erp_id: string;
    name: string;
    status?: SrCustomerStatus;
    line1?: string;
    line2?: string;
    line3?: string;
    street?: string;
    zip_code?: string;
    city?: string;
    country_code?: string;
    match_type: SearchMatchType;
    match_reasons: SearchMatchReason[];
} & SearchTrackingFields;

export type ProductSearchRow = {
    id: string;
    product_variant_id: string;
    product_variant_name: string;
    article_group_id: string;
    article_group_name: string;
    ean: string;
    gtin: string;
    package_unit: string;
    parts_list_type: string;
    status: string;
    partial_packing_type: string;
    supplier_id: string;
    supplier_name: string;
    purchase_order_number: string;
    match_type: SearchMatchType;
    quantity_unit_sales?: string | null;
    sales_units_per_package_unit?: number | null;
} & SearchTrackingFields;

export type SalesOrderSearchRow = {
    id: string;
    erp_id: string;
    type: string;
    document_type: string;
    customer_id: string;
    customer_name?: string;
    match_type: SearchMatchType;
} & SearchTrackingFields;

export enum SearchEntity {
    Product = 'product',
    Customer = 'customer',
    SalesOrder = 'sales_order',
    SalesQuote = 'sales_quote',
}

export enum GlobalSearchTab {
    All = 'all',
    Customers = 'customers',
    Orders = 'sales_orders',
    Quotes = 'sales_quotes',
    Products = 'products',
}

export const SearchEntityTab = {
    [SearchEntity.Customer]: GlobalSearchTab.Customers,
    [SearchEntity.SalesOrder]: GlobalSearchTab.Orders,
    [SearchEntity.SalesQuote]: GlobalSearchTab.Quotes,
    [SearchEntity.Product]: GlobalSearchTab.Products,
} as const;

export const SearchEntityRoute = {
    [SearchEntity.Customer]: '/customers',
    [SearchEntity.SalesOrder]: '/orders',
    [SearchEntity.SalesQuote]: '/quotes',
    [SearchEntity.Product]: '/products',
} as const;

type CustomerSearchEntity = {
    entity: SearchEntity.Customer;
} & CustomerSearchRow;

type OrderSearchEntity = {
    entity: SearchEntity.SalesOrder;
} & SalesOrderSearchRow;

type QuoteSearchEntity = {
    entity: SearchEntity.SalesQuote;
} & SalesOrderSearchRow;

type ProductSearchEntity = {
    entity: SearchEntity.Product;
} & ProductSearchRow;

export type SearchEntityItemData = {
    [SearchEntity.Customer]: CustomerSearchEntity;
    [SearchEntity.SalesOrder]: OrderSearchEntity;
    [SearchEntity.SalesQuote]: QuoteSearchEntity;
    [SearchEntity.Product]: ProductSearchEntity;
};
